import type { ButtonHTMLAttributes, ReactNode } from 'react'
import './UIButton.css'

export type UIButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'
export type UIButtonSize = 'sm' | 'md' | 'lg'

export interface UIButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: UIButtonVariant
  size?: UIButtonSize
  isLoading?: boolean
  /** Stretch to fill its container (useful in stacked mobile dialog footers). */
  fullWidth?: boolean
  children?: ReactNode
}

/**
 * Token-based button primitive (KAN-1179). Unlike the legacy `common/Button.tsx`
 * (which emits Tailwind utility classes not compiled in this project), this button
 * styles itself entirely from the design tokens, so it is theme-aware (light/dark)
 * and consistent across portals. Used by the dialog primitives.
 */
export function UIButton({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  className = '',
  disabled,
  children,
  type = 'button',
  ...rest
}: UIButtonProps) {
  const classes = [
    'ui-btn',
    `ui-btn--${variant}`,
    `ui-btn--${size}`,
    fullWidth ? 'ui-btn--block' : '',
    isLoading ? 'ui-btn--loading' : '',
    className
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button className={classes} type={type} disabled={disabled || isLoading} aria-busy={isLoading} {...rest}>
      {isLoading && <span className="ui-btn__spinner" aria-hidden="true" />}
      <span className="ui-btn__label">{children}</span>
    </button>
  )
}

export default UIButton
